// The week against the field (scenarios Q4 and Q5, B-360, B-361): every team's
// score this week in order, mine marked. The scoreboard of a league with no
// opponents, and the side of its Game Day. All-play adds each team's week
// record, *7–2*, which is the result there; total points has none.

import { Link, useNavigate } from 'react-router-dom';
import type { FieldRowView } from '@fsp/domain';
import { useLeagueBase } from '../../data/hooks';
import { useAtLeast } from '../../data/detail';
import { EmptyState, TeamMark } from '../../kit';
import { Chip, Text } from '../../kit/ui';
import { num } from '../shared/notComputed';
import { ordinal } from '../shared/RosterMoveSheet';
import '../../styles/screens/game-day.css';
import { useTools } from '../../data/tools';

/** A week's all-play record: the result of a week in an all-play league. */
export function weekRecord(r: FieldRowView): string {
  return `${r.week.wins}–${r.week.losses}${r.week.ties ? `–${r.week.ties}` : ''}`;
}

/** How many teams play the week: this row and every team its record counts. */
export function teamsIn(r: FieldRowView): number {
  return 1 + r.week.wins + r.week.losses + r.week.ties;
}

/**
 * Whether the week has a place to tell: settled, or scores that differ. A
 * week under way with everybody still level, at 0 before the first lines,
 * puts nobody first.
 */
export function begun(r: FieldRowView, of: number): boolean {
  return r.status === 'final' || (r.status === 'live' && r.week.ties < of - 1);
}

/** "3rd of 10", or null before the scores tell anybody apart. */
export function placeLine(r: FieldRowView, of: number): string | null {
  return begun(r, of) ? `${ordinal(r.place)} of ${of}` : null;
}

/**
 * Where my score stands against an elimination week's cut (Q1, B-364): the
 * lowest score still safe against the highest at the cut. Null when the week
 * has no cut to speak of.
 */
export function cutLine(row: FieldRowView, rows: readonly FieldRowView[]): string | null {
  if (row.cut === null) return null;
  if (row.status === 'final') return row.cut === 'out' ? 'Out this week' : 'Safe this week';
  const danger = rows.filter((r) => r.cut === 'danger').map((r) => r.score);
  const safe = rows.filter((r) => r.cut === 'safe').map((r) => r.score);
  if (row.cut === 'danger') {
    const need = safe.length === 0 ? 0 : Math.min(...safe) - row.score;
    return `At the cut so far${need > 0 ? `, ${need.toFixed(1)} short of safe` : ''}`;
  }
  const over = danger.length === 0 ? 0 : row.score - Math.max(...danger);
  return `Safe so far${over > 0 ? ` by ${over.toFixed(1)}` : ''}`;
}

export function FieldTable({
  rows,
  allPlay,
}: {
  rows: readonly FieldRowView[];
  /** All-play: each row carries its week record. */
  allPlay: boolean;
}) {
  const tools = useTools();
  const base = useLeagueBase();
  const navigate = useNavigate();
  // The projection is Standard's number, as on a head-to-head card.
  const standard = useAtLeast('standard');
  if (rows.length === 0) return <EmptyState>No scores this week.</EmptyState>;
  const started = rows.some((r) => begun(r, rows.length));
  return (
    <div className="fsp-field" role="list" aria-label="This week’s scores">
      {rows.map((r) => (
        <div
          key={r.team.id}
          role="listitem"
          className={`fsp-row fsp-row-clickable fsp-field-row${r.team.isMine ? ' fsp-row-selected' : ''}`}
          title={`${r.team.name}'s roster`}
          data-ux-convenience=""
          onClick={() => navigate(`${base}/teams/${r.team.id}`)}
        >
          <span className="fsp-num fsp-muted fsp-field-place">{started ? r.place : '—'}</span>
          <TeamMark team={r.team} size={26} />
          <div className="fsp-field-who">
            <div className="fsp-name">
              <span className="fsp-name-text">{r.team.name}</span>
              {/* THE CUT (Q1, B-364): who went out, and who is below it so far. */}
              {r.cut === 'out' ? (
                <Chip tone="bad">Out</Chip>
              ) : r.cut === 'danger' ? (
                <Chip tone="warn">At the cut</Chip>
              ) : null}
            </div>
            <div className="fsp-sub">
              {r.team.ownerName}
              {allPlay && started ? ` · ${weekRecord(r)} this week` : ''}
            </div>
          </div>
          <div className="fsp-right">
            <Text weight="strong" numeric>
              {started ? r.score.toFixed(1) : '—'}
            </Text>
            {standard && r.status !== 'final' && tools.projections ? (
              <div className="fsp-sub fsp-num">proj {num(r.projected)}</div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * My week against the field, compact (Home's "This week", a league's hub
 * card): my score and what it comes to so far, opening Game Day. The card's
 * numbers are the last scoring pass's; a projection is Game Day's.
 */
export function FieldWeekCard({
  row,
  of,
  allPlay,
  leagueId,
}: {
  row: FieldRowView;
  of: number;
  allPlay: boolean;
  /** The league this week belongs to; defaults to the active league. The hub passes it. */
  leagueId?: string;
}) {
  const activeBase = useLeagueBase();
  const base = leagueId ? `/l/${leagueId}` : activeBase;
  const place = placeLine(row, of);
  const so = row.status === 'final' ? '' : ' so far';
  return (
    <Link
      to={`${base}/matchup?period=${row.period}`}
      className="fsp-surface fsp-field-weekcard"
      aria-label={`${row.team.name}'s week against the league`}
    >
      <TeamMark team={row.team} size={28} />
      <div className="fsp-field-who">
        <div className="fsp-name">
          <span className="fsp-name-text">{row.team.name}</span>
        </div>
        <div className="fsp-sub">
          {place === null
            ? 'Against the whole league this week'
            : `${place}${so}${allPlay ? ` · ${weekRecord(row)}` : ''}`}
        </div>
      </div>
      <Text weight="strong" numeric>
        {row.status === 'pre' ? '—' : row.score.toFixed(1)}
      </Text>
    </Link>
  );
}
